import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Badge, Button, Card, EmptyState, Input, Select, Textarea, cn } from "../../../components/ui";
import Modal from "../../../components/base/Modal";
import { exchangeApi, apiErrorMessage, type CheckIn, type Trip, type TripStatus, type TrustLevel, type Place } from "./api";
import {
  CITY_OPTIONS,
  INCIDENT_LABELS,
  PAYMENT_LABELS,
  TRIP_ICONS,
  TRIP_LABELS,
  TRIP_STEPS,
  TRUST_META,
  UNIT_LABELS,
  fmtDateTime,
  fmtWindow,
  money,
  nextStep,
  pct,
  tripTone,
} from "./format";
import { useMe } from "./useMe";

// ===========================================================================
// Cargo Market — piezas compartidas por los espacios Embarcador, Transportista
// y Administración. Siguen el estándar de UI de v5 (components/ui).
// ===========================================================================

export const CM_BASE = "/cargo-market";

/** Contenedor de sección: header + contenido con scroll. */
export function Section({ title, subtitle, actions, filters, children }: { title: string; subtitle?: string; actions?: ReactNode; filters?: ReactNode; children: ReactNode }) {
  return (
    <div className="h-full w-full flex flex-col">
      <div className="bg-surface border-b border-line px-4 sm:px-6 pt-4 sm:pt-6 pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-content">{title}</h1>
            {subtitle && <p className="text-sm text-content-muted mt-1">{subtitle}</p>}
          </div>
          {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
        </div>
        {filters && <div className="mt-4">{filters}</div>}
      </div>
      <div className="flex-1 overflow-y-auto p-3 sm:p-6">{children}</div>
    </div>
  );
}

export function Loader() {
  return (
    <div className="flex min-h-64 items-center justify-center py-12">
      <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: string; onRetry?: () => void }) {
  return (
    <EmptyState
      icon="ri-error-warning-line"
      title="No se pudo cargar la información"
      description={error}
      action={onRetry ? <Button size="sm" variant="outline" onClick={onRetry} icon={<i className="ri-refresh-line" />}>Reintentar</Button> : undefined}
    />
  );
}

export function TrustBadge({ level, size = "md" }: { level: TrustLevel; size?: "sm" | "md" }) {
  const m = TRUST_META[level] ?? TRUST_META.registrado;
  return (
    <span title={m.hint} className="inline-flex">
      <Badge tone={m.tone} size={size}>
        <i className={cn(m.icon, "mr-1")} />
        {m.label}
      </Badge>
    </span>
  );
}

export function TripStatusBadge({ status }: { status: TripStatus }) {
  return (
    <Badge tone={tripTone(status)}>
      <i className={cn(TRIP_ICONS[status], "mr-1")} />
      {TRIP_LABELS[status]}
    </Badge>
  );
}

export function RouteLine({ origin, destination, className }: { origin: Pick<Place, "city" | "state">; destination: Pick<Place, "city" | "state"> | null; className?: string }) {
  return (
    <div className={cn("flex items-center gap-2 min-w-0", className)}>
      <span className="truncate font-semibold text-content">{origin.city}<span className="text-content-faint font-normal">, {origin.state}</span></span>
      <i className="ri-arrow-right-line text-primary-500 shrink-0" />
      <span className="truncate font-semibold text-content">
        {destination ? <>{destination.city}<span className="text-content-faint font-normal">, {destination.state}</span></> : <span className="text-content-muted font-normal">Cualquier destino</span>}
      </span>
    </div>
  );
}

export function Kpi({ label, value, icon, hint, tone = "primary" }: { label: string; value: ReactNode; icon: string; hint?: ReactNode; tone?: "primary" | "success" | "warning" | "info" | "error" }) {
  const tones = {
    primary: "bg-primary-500/10 text-primary-500",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning",
    info: "bg-info/10 text-info",
    error: "bg-error/10 text-error",
  };
  return (
    <Card padding="md">
      <div className="flex items-start gap-3">
        <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center shrink-0", tones[tone])}>
          <i className={cn(icon, "text-lg")} />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-content-muted">{label}</p>
          <p className="text-xl font-semibold text-content leading-tight mt-0.5">{value}</p>
          {hint && <p className="text-xs text-content-faint mt-1">{hint}</p>}
        </div>
      </div>
    </Card>
  );
}

export function CitySelect({ label, value, onChange, required, allowAny, error }: { label: string; value: string; onChange: (v: string) => void; required?: boolean; allowAny?: boolean; error?: string }) {
  return (
    <Select
      label={<>{label}{required && <span className="text-error"> *</span>}</>}
      value={value}
      error={error}
      onChange={(e) => onChange(e.target.value)}
      options={[{ value: "", label: allowAny ? "Cualquier destino" : "Elige una ciudad" }, ...CITY_OPTIONS]}
    />
  );
}

/** Pasos del viaje con Centinela (8 estados). */
export function TripStepper({ trip }: { trip: Pick<Trip, "status" | "events"> }) {
  const current = TRIP_STEPS.indexOf(trip.status);
  const at = (s: TripStatus) => trip.events.find((e) => e.status === s)?.at;
  if (trip.status === "cancelled") return <Badge tone="error">Viaje cancelado</Badge>;
  return (
    <ol className="grid grid-cols-4 lg:grid-cols-8 gap-2">
      {TRIP_STEPS.map((s, i) => {
        const done = current >= 0 && i <= current;
        const active = i === current;
        return (
          <li key={s} className={cn("rounded-lg border px-2 py-2 text-center", active ? "border-primary-500 bg-primary-500/10" : done ? "border-success/30 bg-success/10" : "border-line bg-app")}>
            <i className={cn(TRIP_ICONS[s], "text-base", active ? "text-primary-500" : done ? "text-success" : "text-content-faint")} />
            <p className={cn("text-[11px] leading-tight mt-1", done ? "text-content" : "text-content-muted")}>{TRIP_LABELS[s]}</p>
            {at(s) && <p className="text-[10px] text-content-faint mt-0.5">{fmtDateTime(at(s)).split(" · ")[1]}</p>}
          </li>
        );
      })}
    </ol>
  );
}

/** Aviso cuando la compañía aún no tiene ficha o no está verificada para un rol. */
export function GateNotice({ role }: { role: "carrier" | "shipper" }) {
  const { me } = useMe();
  const navigate = useNavigate();
  if (!me) return null;
  const hasRole = me.profile?.roles?.[role];
  const verified = role === "carrier" ? me.carrier.canOperate : me.shipper.canOperate;
  if (hasRole && verified) return null;
  const target = role === "carrier" ? `${CM_BASE}/transportista/verificacion` : `${CM_BASE}/embarcador/verificacion`;
  return (
    <div className="mb-4 rounded-xl border border-warning/30 bg-warning/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <i className="ri-shield-keyhole-line text-2xl text-warning shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-content">
          {!me.profile ? "Da de alta a tu empresa en Cargo Market" : !hasRole ? `Activa el rol de ${role === "carrier" ? "transportista" : "embarcador"}` : "Completa tu verificación para operar"}
        </p>
        <p className="text-xs text-content-muted mt-0.5">
          {role === "carrier"
            ? "Sin el sello «Verificado» (empresa + una unidad + un operador con documentos vigentes) no puedes tomar cargas ni publicar capacidad."
            : "Verificamos a los embarcadores para que el transportista sepa que le van a pagar y que la carga es legal."}
        </p>
      </div>
      <Button size="sm" onClick={() => navigate(target)} icon={<i className="ri-arrow-right-line" />}>Ir a verificación</Button>
    </div>
  );
}

// --- Detalle del viaje ---------------------------------------------------------

const CHECK_IN_OPTIONS: { value: CheckIn; label: string }[] = [
  { value: "geofence", label: "Geocerca (automático por GPS)" },
  { value: "qr", label: "Código QR del almacén" },
  { value: "pin", label: "PIN que da el almacén" },
];

function AdvanceForm({ trip, to, onDone, onCancel }: { trip: Trip; to: TripStatus; onDone: (t: Trip) => void; onCancel: () => void }) {
  const [checkIn, setCheckIn] = useState<CheckIn>("geofence");
  const [signedBy, setSignedBy] = useState("");
  const [sealNumber, setSealNumber] = useState("");
  const [sealIntact, setSealIntact] = useState(true);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const needsCheckIn = to === "at_pickup" || to === "at_dropoff";
  const needsSeal = to === "loaded";
  const needsPod = to === "delivered";

  const submit = async () => {
    setSaving(true);
    try {
      const t = await exchangeApi.advanceTrip({
        tripId: trip.id,
        status: to,
        note: note || undefined,
        evidence: { checkIn: needsCheckIn ? checkIn : undefined, signedBy: signedBy || undefined, sealNumber: sealNumber || undefined, sealIntact: needsPod ? sealIntact : undefined },
      });
      toast.success(`${trip.folio}: ${TRIP_LABELS[to]}`);
      onDone(t);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-content-muted">
        {to === "closed"
          ? "Al cerrar confirmas la entrega (POD): se calculan las estadías y se libera el pago al transportista."
          : `Marca el viaje como «${TRIP_LABELS[to]}». Centinela avisa a la otra parte.`}
      </p>
      {needsCheckIn && <Select label="Check-in" value={checkIn} onChange={(e) => setCheckIn(e.target.value as CheckIn)} options={CHECK_IN_OPTIONS} />}
      {(needsSeal || needsPod) && <Input label={<>{needsPod ? "Quién recibe (firma del POD)" : "Quién entrega la carga (firma del remitente)"}<span className="text-error"> *</span></>} value={signedBy} onChange={(e) => setSignedBy(e.target.value)} />}
      {needsSeal && <Input label={<>Número de sello o candado<span className="text-error"> *</span></>} value={sealNumber} onChange={(e) => setSealNumber(e.target.value)} />}
      {needsPod && (
        <label className="flex items-center gap-2 text-sm text-content">
          <input type="checkbox" checked={sealIntact} onChange={(e) => setSealIntact(e.target.checked)} className="accent-primary-500" />
          Sello intacto al entregar (si no, se levanta incidencia)
        </label>
      )}
      <Textarea label="Nota (opcional)" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex gap-3 border-t border-line pt-4">
        <Button variant="outline" shape="pill" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button shape="pill" className="flex-1" loading={saving} onClick={submit}>{TRIP_LABELS[to]}</Button>
      </div>
    </div>
  );
}

function IncidentForm({ trip, onDone, onCancel }: { trip: Trip; onDone: (t: Trip) => void; onCancel: () => void }) {
  const [type, setType] = useState("delay");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const t = await exchangeApi.incident({ tripId: trip.id, type, note });
      toast.success("Incidencia registrada");
      onDone(t);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      <Select label="Tipo" value={type} onChange={(e) => setType(e.target.value)} options={Object.entries(INCIDENT_LABELS).filter(([k]) => k !== "seal_broken").map(([value, label]) => ({ value, label }))} />
      <Textarea label="¿Qué pasó?" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
      <div className="flex gap-3 border-t border-line pt-4">
        <Button variant="outline" shape="pill" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button variant="danger" shape="pill" className="flex-1" loading={saving} onClick={submit}>Registrar incidencia</Button>
      </div>
    </div>
  );
}

function RateForm({ trip, onDone, onCancel }: { trip: Trip; onDone: (t: Trip) => void; onCancel: () => void }) {
  const [score, setScore] = useState(5);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      const t = await exchangeApi.rateTrip({ tripId: trip.id, score, comment });
      toast.success("Gracias por calificar");
      onDone(t);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      <p className="text-sm text-content-muted">Califica a {trip.role === "shipper" ? trip.carrierName : trip.shipperName}. Tu calificación cuenta para su nivel Premium.</p>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" aria-label={`${n} estrellas`} onClick={() => setScore(n)} className="text-3xl leading-none">
            <i className={n <= score ? "ri-star-fill text-warning" : "ri-star-line text-content-faint"} />
          </button>
        ))}
      </div>
      <Textarea label="Comentario (opcional)" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
      <div className="flex gap-3 border-t border-line pt-4">
        <Button variant="outline" shape="pill" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button shape="pill" className="flex-1" loading={saving} onClick={submit}>Calificar</Button>
      </div>
    </div>
  );
}

function AssignForm({ trip, onDone, onCancel }: { trip: Trip; onDone: (t: Trip) => void; onCancel: () => void }) {
  const { me, reload } = useMe();
  const [vehicleId, setVehicleId] = useState(trip.vehicleId || "");
  const [remolqueId, setRemolqueId] = useState(trip.remolqueId || "");
  const [driverId, setDriverId] = useState(trip.driverId || "");
  const [saving, setSaving] = useState(false);
  if (!me) return <Loader />;
  const units = me.carrier.units;
  const unitOk = (id: string) => units.find((u) => u.id === id);
  const driverOk = (id: string) => me.carrier.drivers.find((d) => d.id === id);
  const vehicleOptions = me.fleet.vehicles.map((v) => {
    const c = unitOk(v.id);
    const sameType = v.unitType === trip.unitType;
    const tag = !c?.ok ? " · expediente incompleto" : !sameType ? ` · es ${UNIT_LABELS[v.unitType]}` : v.deviceId ? " · con GPS" : " · sin GPS";
    return { value: v.id, label: `${v.name || v.plate || v.id}${v.plate ? ` (${v.plate})` : ""}${tag}`, disabled: !c?.ok || !sameType };
  });
  const driverOptions = me.fleet.drivers.map((d) => ({ value: d.id, label: `${d.name}${driverOk(d.id)?.ok ? "" : " · expediente incompleto"}`, disabled: !driverOk(d.id)?.ok }));
  const submit = async () => {
    setSaving(true);
    try {
      const t = await exchangeApi.assignTrip({ tripId: trip.id, vehicleId, remolqueId: remolqueId || null, driverId });
      toast.success("Viaje confirmado y creado en Administración de Flotilla");
      void reload();
      onDone(t);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-info/30 bg-info/10 p-3 text-xs text-content">
        <i className="ri-links-line mr-1 text-info" />
        Se usan las unidades, remolques y operadores de tu flotilla. Al confirmar se crea el viaje en <b>Administración de Flotilla › Viajes</b> con el GPS de la unidad, y Centinela lo vigila.
      </div>
      <Select label={<>Unidad ({UNIT_LABELS[trip.unitType]})<span className="text-error"> *</span></>} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} options={[{ value: "", label: "Elige una unidad" }, ...vehicleOptions]} />
      <Select label="Remolque (opcional)" value={remolqueId} onChange={(e) => setRemolqueId(e.target.value)} options={[{ value: "", label: "Sin remolque" }, ...me.fleet.trailers.map((t) => ({ value: t.id, label: `${t.name || t.id}${t.plate ? ` (${t.plate})` : ""}` }))]} />
      <Select label={<>Operador<span className="text-error"> *</span></>} value={driverId} onChange={(e) => setDriverId(e.target.value)} options={[{ value: "", label: "Elige un operador" }, ...driverOptions]} />
      <div className="flex gap-3 border-t border-line pt-4">
        <Button variant="outline" shape="pill" className="flex-1" onClick={onCancel}>Cancelar</Button>
        <Button shape="pill" className="flex-1" loading={saving} disabled={!vehicleId || !driverId} onClick={submit}>Confirmar viaje</Button>
      </div>
    </div>
  );
}

type TripDialog = { kind: "advance"; to: TripStatus } | { kind: "incident" } | { kind: "rate" } | { kind: "assign" } | { kind: "cancel" } | null;

/** Modal con todo el viaje: pasos, unidad, operador, precio, pago, evidencias y acciones. */
export function TripDetailModal({ trip: initial, onClose, onChanged }: { trip: Trip; onClose: () => void; onChanged?: (t: Trip) => void }) {
  const navigate = useNavigate();
  const [trip, setTrip] = useState(initial);
  const [dialog, setDialog] = useState<TripDialog>(null);
  const done = (t: Trip) => {
    setTrip(t);
    setDialog(null);
    onChanged?.(t);
  };
  const step = nextStep(trip.status);
  const mine = step && step.actor === trip.role;
  const isCarrier = trip.role === "carrier";
  const canRate = ["delivered", "closed"].includes(trip.status) && !trip.ratings?.[trip.role];
  const canCancel = ["awarded", "confirmed", "to_pickup", "at_pickup"].includes(trip.status);
  const payment = PAYMENT_LABELS[trip.payment?.status] ?? { label: trip.payment?.status, tone: "neutral" as const };
  const contact = isCarrier ? trip.shipperContact : trip.carrierContact;

  const body = dialog ? (
    dialog.kind === "advance" ? (
      <AdvanceForm trip={trip} to={dialog.to} onDone={done} onCancel={() => setDialog(null)} />
    ) : dialog.kind === "cancel" ? (
      <AdvanceForm trip={trip} to="cancelled" onDone={done} onCancel={() => setDialog(null)} />
    ) : dialog.kind === "incident" ? (
      <IncidentForm trip={trip} onDone={done} onCancel={() => setDialog(null)} />
    ) : dialog.kind === "rate" ? (
      <RateForm trip={trip} onDone={done} onCancel={() => setDialog(null)} />
    ) : (
      <AssignForm trip={trip} onDone={done} onCancel={() => setDialog(null)} />
    )
  ) : (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <TripStatusBadge status={trip.status} />
        <Badge tone={payment.tone}>{payment.label}</Badge>
        {trip.cartaPorte?.status === "generated" && <Badge tone="neutral"><i className="ri-file-text-line mr-1" />Carta Porte {trip.cartaPorte.folio}</Badge>}
        {trip.sandboxOf && <Badge tone="warning">Ejemplo (Sandbox)</Badge>}
      </div>
      <RouteLine origin={trip.origin} destination={trip.destination} className="text-base" />
      <TripStepper trip={trip} />

      {trip.status === "awarded" && (
        <div className="rounded-xl border border-warning/30 bg-warning/10 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <i className="ri-user-add-line text-2xl text-warning" />
          <p className="flex-1 text-sm text-content">{isCarrier ? "Asigna unidad y operador de tu flotilla para confirmar el viaje y generar la Carta Porte." : `${trip.carrierName} está asignando unidad y operador.`}</p>
          {isCarrier && <Button size="sm" onClick={() => setDialog({ kind: "assign" })}>Asignar unidad</Button>}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <Card padding="md">
          <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">Carga</p>
          <dl className="space-y-1.5 text-sm">
            <Row k="Mercancía" v={`${trip.merchandise}${trip.satKey ? ` · SAT ${trip.satKey}` : ""}`} />
            <Row k="Peso / volumen" v={`${trip.weightTons} t${trip.volumeM3 ? ` · ${trip.volumeM3} m³` : ""}`} />
            <Row k="Unidad requerida" v={UNIT_LABELS[trip.unitType]} />
            <Row k="Carga" v={fmtWindow(trip.pickupWindow)} />
            <Row k="Entrega" v={fmtWindow(trip.deliveryWindow)} />
            <Row k="Distancia" v={`${trip.km} km aprox.`} />
          </dl>
        </Card>
        <Card padding="md">
          <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">Precio</p>
          <dl className="space-y-1.5 text-sm">
            {isCarrier ? <Row k="Tu flete" v={money(trip.pricing.freight)} strong /> : <Row k="Total" v={money(trip.pricing.total)} strong />}
            <Row k={`Comisión WAYAK (${pct(trip.pricing.commissionPct)})`} v={money(trip.pricing.commission)} />
            {!isCarrier && <Row k="Flete al transportista" v={money(trip.pricing.freight)} />}
            {trip.detention && <Row k={`Estadías (${trip.detention.billableHours} h)`} v={money(trip.detention.amount)} />}
            <Row k="Pago" v={trip.payment.mode === "credit" ? `Crédito a ${trip.payment.days} días` : "Por adelantado (escrow)"} />
          </dl>
        </Card>
        <Card padding="md">
          <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">Unidad y operador</p>
          {trip.unit ? (
            <dl className="space-y-1.5 text-sm">
              <Row k="Unidad" v={`${trip.unit.name || "—"}${trip.unit.economicNumber ? ` · ${trip.unit.economicNumber}` : ""}`} />
              <Row k="Placas" v={trip.unit.plate || "—"} />
              {trip.unit.trailer && <Row k="Remolque" v={`${trip.unit.trailer.name || ""} ${trip.unit.trailer.plate ? `(${trip.unit.trailer.plate})` : ""}`} />}
              <Row k="Operador" v={trip.driver?.name || "—"} />
              <Row k="Teléfono" v={trip.driver?.phone || (trip.contactsRevealed ? "—" : "Se revela al confirmar")} />
              <Row k="GPS" v={trip.unit.hasGps ? "Integrado a WAYAK" : "Sin GPS"} />
            </dl>
          ) : (
            <p className="text-sm text-content-muted">Pendiente de asignar.</p>
          )}
        </Card>
        <Card padding="md">
          <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">{isCarrier ? "Embarcador" : "Transportista"}</p>
          <dl className="space-y-1.5 text-sm">
            <Row k="Empresa" v={isCarrier ? trip.shipperName : trip.carrierName} />
            <Row k="Contacto" v={contact?.name || (trip.contactsRevealed ? "—" : "Se revela al confirmar")} />
            <Row k="Teléfono" v={contact?.phone || (trip.contactsRevealed ? "—" : "Se revela al confirmar")} />
          </dl>
        </Card>
      </div>

      {(trip.fleetTripId || trip.deviceId) && isCarrier && (
        <div className="flex flex-wrap gap-2">
          {trip.fleetTripId && <Button size="sm" variant="infoSoft" icon={<i className="ri-route-line" />} onClick={() => navigate("/admin/trips")}>Ver en Administración de Flotilla</Button>}
          {trip.deviceId && <Button size="sm" variant="soft" icon={<i className="ri-robot-2-line" />} onClick={() => navigate("/monitoreo-ia/unidades")}>Ver en Centinela</Button>}
        </div>
      )}

      {trip.incidents?.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">Incidencias</p>
          <ul className="space-y-2">
            {trip.incidents.map((i, n) => (
              <li key={n} className="rounded-lg border border-error/30 bg-error/10 px-3 py-2 text-sm">
                <b>{INCIDENT_LABELS[i.type] || i.type}</b> · {fmtDateTime(i.at)}{i.note ? ` — ${i.note}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <p className="text-xs font-semibold text-content-muted uppercase tracking-wide mb-2">Bitácora (evidencia)</p>
        <ul className="space-y-1.5">
          {[...trip.events].reverse().map((e, n) => (
            <li key={n} className="flex gap-2 text-sm">
              <i className={cn(TRIP_ICONS[e.status], "text-primary-500 mt-0.5")} />
              <div className="min-w-0">
                <span className="font-medium text-content">{TRIP_LABELS[e.status]}</span>
                <span className="text-content-faint"> · {fmtDateTime(e.at)}</span>
                {(e.note || e.evidence?.signedBy || e.evidence?.sealNumber || e.evidence?.checkIn) && (
                  <p className="text-xs text-content-muted">
                    {[e.note, e.evidence?.checkIn && `check-in por ${e.evidence.checkIn === "geofence" ? "geocerca" : e.evidence.checkIn.toUpperCase()}`, e.evidence?.signedBy && `firma: ${e.evidence.signedBy}`, e.evidence?.sealNumber && `sello ${e.evidence.sealNumber}`, e.evidence?.sealIntact === false && "sello NO íntegro"].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {(Object.keys(trip.ratings || {}).length > 0) && (
        <div className="text-sm text-content-muted">
          {trip.ratings.shipper && <p>Embarcador calificó: {"★".repeat(trip.ratings.shipper.score)} {trip.ratings.shipper.comment}</p>}
          {trip.ratings.carrier && <p>Transportista calificó: {"★".repeat(trip.ratings.carrier.score)} {trip.ratings.carrier.comment}</p>}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-t border-line pt-4">
        {mine && step && <Button size="sm" icon={<i className={TRIP_ICONS[step.status]} />} onClick={() => setDialog({ kind: "advance", to: step.status })}>{step.status === "closed" ? "Confirmar entrega y cerrar" : TRIP_LABELS[step.status]}</Button>}
        {step && !mine && trip.status !== "awarded" && <Badge tone="neutral">Siguiente paso: {TRIP_LABELS[step.status]} ({step.actor === "carrier" ? "transportista" : "embarcador"})</Badge>}
        {isCarrier && trip.status === "confirmed" && <Button size="sm" variant="outline" onClick={() => setDialog({ kind: "assign" })}>Cambiar unidad</Button>}
        {canRate && <Button size="sm" variant="warningSoft" icon={<i className="ri-star-line" />} onClick={() => setDialog({ kind: "rate" })}>Calificar</Button>}
        {!["closed", "cancelled"].includes(trip.status) && <Button size="sm" variant="dangerSoft" icon={<i className="ri-alarm-warning-line" />} onClick={() => setDialog({ kind: "incident" })}>Incidencia</Button>}
        {canCancel && <Button size="sm" variant="ghost" onClick={() => setDialog({ kind: "cancel" })}>Cancelar viaje</Button>}
      </div>
    </div>
  );

  const titles: Record<string, string> = { advance: "Avanzar viaje", incident: "Registrar incidencia", rate: "Calificar", assign: "Asignar unidad y operador", cancel: "Cancelar viaje" };
  return (
    <Modal title={dialog ? `${titles[dialog.kind]} · ${trip.folio}` : `Viaje ${trip.folio}`} onClose={dialog ? () => setDialog(null) : onClose} width={dialog ? "w-full max-w-xl" : "w-full max-w-4xl"} content={body} />
  );
}

function Row({ k, v, strong }: { k: string; v: ReactNode; strong?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-content-muted shrink-0">{k}</dt>
      <dd className={cn("text-right text-content min-w-0 truncate", strong && "font-semibold")}>{v}</dd>
    </div>
  );
}

/** Tabla/tarjeta de viajes compartida (embarcador y transportista). */
export function TripList({ trips, onOpen }: { trips: Trip[]; onOpen: (t: Trip) => void }) {
  if (!trips.length) return <EmptyState icon="ri-route-line" title="Sin viajes todavía" description="Cuando una carga se asigne aparecerá aquí con sus 8 pasos." />;
  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
      {trips.map((t) => {
        const step = nextStep(t.status);
        const yourTurn = t.status === "awarded" ? t.role === "carrier" : step?.actor === t.role;
        return (
          <button key={t.id} type="button" onClick={() => onOpen(t)} className="text-left rounded-2xl border border-line bg-card hover:border-primary-500/60 transition-colors flex flex-col">
            <div className="p-4 flex items-start justify-between gap-2 border-b border-line">
              <div className="min-w-0">
                <p className="text-xs text-content-faint">{t.folio} · {t.role === "carrier" ? t.shipperName : t.carrierName}</p>
                <RouteLine origin={t.origin} destination={t.destination} className="mt-1 text-sm" />
              </div>
              <TripStatusBadge status={t.status} />
            </div>
            <div className="p-4 flex-1 space-y-1 text-sm text-content-muted">
              <p><i className="ri-calendar-line mr-1" />{fmtWindow(t.pickupWindow)}</p>
              <p><i className="ri-box-3-line mr-1" />{t.merchandise} · {t.weightTons} t · {UNIT_LABELS[t.unitType]}</p>
              {t.unit && <p><i className="ri-truck-line mr-1" />{t.unit.name}{t.driver ? ` · ${t.driver.name}` : ""}</p>}
            </div>
            <div className="px-4 py-3 border-t border-line flex items-center justify-between gap-2">
              <span className="font-semibold text-content">{money(t.role === "carrier" ? t.pricing.freight : t.pricing.total)}</span>
              {yourTurn && !["closed", "cancelled"].includes(t.status) ? <Badge tone="warning" dot>Te toca</Badge> : t.incidents?.length ? <Badge tone="error">{t.incidents.length} incidencia(s)</Badge> : null}
            </div>
          </button>
        );
      })}
    </div>
  );
}
